import { format } from "date-fns";
import * as React from "react";

import {
    Breakpoint,
    createError,
    RelodeApiError,
    useAsyncEffect,
    useEffectAfterMount,
    useRelodeApp,
    useRouter,
    useNotifications,
} from "../../../../";
import { useMessageCheckerContext } from "../../messageChecker/messageCheckerProvider";
import { MessageComposer } from "../../messageComposer/messageComposer";
import {
    getMessageDetails,
    setMessagesRead,
    setMessagesUnread,
} from "../../messaging.service";
import { MessageRecipient } from "../../types";
import { messageDetailsReducer } from "../messageDetailsReducer";
import { initialMessageDetailsState } from "../messageDetailsState";
import { MessageDetailDesktop } from "./messageDetailDesktop";
import { MessageDetailMobile } from "./messageDetailMobile";

export const MessageDetail = () => {
    const [state, dispatch] = React.useReducer(
        messageDetailsReducer,
        initialMessageDetailsState
    );
    const { actions: notifications } = useNotifications();
    const [isMessageComposerOpen, setMessageComposerOpen] = React.useState(
        false
    );
    const [initialRecipients, setInitialRecipients] = React.useState(
        [] as MessageRecipient[]
    );
    const [initialBodyText, setInitialBodyText] = React.useState("");
    const [actionType, setActionType] = React.useState(undefined);
    const [backUrl, setBackUrl] = React.useState("");

    const {
        state: {
            config: { messageTitleFactory },
        },
    } = useRelodeApp();

    const { currentMessage, messageIds, loadingMessage, currentIndex } = state;

    const {
        history,
        location,
        match: {
            params: { messageId },
        },
    } = useRouter();

    const {
        state: { unreadCount },
        actions: { updateUnreadCount },
    } = useMessageCheckerContext();

    React.useEffect(() => {
        setBackUrl(
            `/messages${
                location.state && location.state.queryString
                    ? "?" + location.state.queryString
                    : ""
            }`
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffectAfterMount(() => {
        const msgId = messageIds[currentIndex];
        if (!!currentMessage && msgId === currentMessage.messageId) return;
        history.push(`/messages/${msgId}`);
    }, [currentIndex]);

    useAsyncEffect(async () => {
        dispatch({ type: "LOAD_MESSAGE" });
        try {
            const message = await getMessageDetails(messageId);
            // how does current index get set without triggering the above effect?
            let ids: number[] = state.messageIds;
            const locState = location.state && location.state.messageIds;
            if (!!locState && Array.isArray(locState)) {
                ids = locState; //.map(i => +i);
            }
            const currentIndex = ids.indexOf(+messageId);
            if (!message.isRead) {
                await setMessagesRead([+messageId]);
                updateUnreadCount(Math.max(unreadCount - 1, 0));
            }
            dispatch({
                type: "LOAD_MESSAGE_SUCCESS",
                message,
                ids,
                currentIndex,
            });
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    notifications.showModal(
                        "error",
                        "not found",
                        "unable to load selected message",
                        {
                            callback: () => {
                                history.replace("/messages");
                            },
                        }
                    );
                    return;
                }
            }
            throw createError(
                err,
                "core.messages.messageDetailsHandlers.useAsyncEffect: load message"
            );
        }
    }, [messageId]);

    useAsyncEffect(async () => {
        if (currentMessage) {
            await setMessagesRead([currentMessage.messageId]);
            updateUnreadCount(Math.max(unreadCount - 1, 0));
            dispatch({ type: "SET_MESSAGES_READ_SUCCESS" });
        }
    }, [state.settingMessageRead]);

    useAsyncEffect(async () => {
        if (currentMessage) {
            await setMessagesUnread([currentMessage.messageId]);
            updateUnreadCount(unreadCount + 1);
            dispatch({ type: "SET_MESSAGES_UNREAD_SUCCESS" });
        }
    }, [state.settingMessageUnread]);

    const handleComposerOpen = (type: "reply" | "forward") => {
        if (!currentMessage) return;

        setActionType(type);

        if (type === "reply") setInitialRecipients([currentMessage.sender]);
        const t = type === "reply" ? "Reply to" : "Forward from";
        setInitialBodyText(
            "\r\n" +
                "---------------------------------------------------------------------------------------------\r\n" +
                `${t} ${currentMessage.sender.displayName} on ${format(
                    new Date(currentMessage.createTimestamp),
                    "P p"
                )}\r\n` +
                "---------------------------------------------------------------------------------------------\r\n" +
                currentMessage.body
        );
        setMessageComposerOpen(true);
    };

    const handleComposerClose = () => {
        setInitialRecipients([]);
        setInitialBodyText("");
        setMessageComposerOpen(false);
    };

    const handleStatus = (action: "read" | "unread") => {
        if (!currentMessage) return;
        switch (action) {
            case "read":
                dispatch({ type: "SET_MESSAGES_READ" });
                break;

            case "unread":
                dispatch({ type: "SET_MESSAGES_UNREAD" });
                break;
        }
    };

    const handleNavigate = React.useCallback(
        (direction: "next" | "prev") => {
            if (direction === "next") {
                dispatch({ type: "NAVIGATE_NEXT" });
            } else {
                dispatch({ type: "NAVIGATE_PREV" });
            }
        },
        [dispatch]
    );

    const getTitle = React.useMemo(() => {
        if (!currentMessage) return null;
        return messageTitleFactory(currentMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMessage]);

    const getLabel = React.useMemo((): string => {
        if (!currentMessage) return null;

        if (!!currentMessage.leadName && !!currentMessage.jobName)
            return `${currentMessage.leadName} - ${currentMessage.jobName} - ${currentMessage.jobLocationName}`;

        if (!!currentMessage.jobName)
            return `${currentMessage.jobName} - ${currentMessage.jobLocationName}`;
        return currentMessage.leadName;
    }, [currentMessage]);

    return (
        <>
            <Breakpoint desktop>
                <MessageDetailDesktop
                    currentMessage={currentMessage}
                    loadingMessage={loadingMessage}
                    handleStatus={handleStatus}
                    handleComposerOpen={handleComposerOpen}
                    handleNavigate={handleNavigate}
                    title={getTitle}
                    label={getLabel}
                    backUrl={backUrl}
                    messageIds={messageIds}
                    currentIndex={currentIndex}
                />
            </Breakpoint>
            <Breakpoint mobile>
                <MessageDetailMobile
                    title={getTitle}
                    currentMessage={currentMessage}
                    loadingMessage={loadingMessage}
                    handleStatus={handleStatus}
                    handleComposerOpen={handleComposerOpen}
                    label={getLabel}
                    backUrl={backUrl}
                    messageIds={messageIds}
                    currentIndex={currentIndex}
                />
            </Breakpoint>
            {isMessageComposerOpen ? (
                <MessageComposer
                    jobId={currentMessage.jobId}
                    jobName={currentMessage.jobName}
                    leadId={currentMessage.leadId}
                    leadName={currentMessage.leadName}
                    open={isMessageComposerOpen}
                    initialRecipients={initialRecipients}
                    initialBodyText={initialBodyText}
                    attachments={
                        actionType === "forward"
                            ? currentMessage.attachments
                            : undefined
                    }
                    onClose={handleComposerClose}
                    replyMessageId={currentMessage.messageId}
                />
            ) : null}
        </>
    );
};
